import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  BillingProcess,
  BillingProcessCreate,
  BillingProcessCustomerGroup,
  BillingProcessFilter,
  BillingProcessInvoicePreview,
  BillingProcessItem,
  BillingProcessPatch,
} from '../models/billing-process';

@Injectable({ providedIn: 'root' })
export class BillingProcessService {
  private readonly baseUrl = environment.apiHost + '/api-orders/secure/billing-process';

  constructor(private http: HttpClient) {}

  list(filter: BillingProcessFilter = {}): Observable<BillingProcess[]> {
    let params = new HttpParams();
    if (filter.status) {
      params = params.set('status', filter.status);
    }
    if (filter.referenceMonth != null) {
      params = params.set('referenceMonth', filter.referenceMonth.toString());
    }
    if (filter.referenceYear != null) {
      params = params.set('referenceYear', filter.referenceYear.toString());
    }
    return this.http.get<BillingProcess[]>(this.baseUrl, { params });
  }

  findById(id: number): Observable<BillingProcess> {
    return this.http.get<BillingProcess>(`${this.baseUrl}/${id}`);
  }

  create(payload: BillingProcessCreate): Observable<BillingProcess> {
    return this.http.post<BillingProcess>(this.baseUrl, payload);
  }

  patch(id: number, payload: BillingProcessPatch): Observable<BillingProcess> {
    return this.http.patch<BillingProcess>(`${this.baseUrl}/${id}`, payload);
  }

  cancel(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  loadEligibleItems(processId: number): Observable<BillingProcessItem[]> {
    return this.http.post<BillingProcessItem[]>(
      `${this.baseUrl}/${processId}/items/load-eligible`, {});
  }

  listItems(processId: number): Observable<BillingProcessItem[]> {
    return this.http.get<BillingProcessItem[]>(`${this.baseUrl}/${processId}/items`);
  }

  updateItemSelection(processId: number, itemId: number, selected: boolean): Observable<BillingProcessItem> {
    const params = new HttpParams().set('selected', String(selected));
    return this.http.patch<BillingProcessItem>(
      `${this.baseUrl}/${processId}/items/${itemId}/selection`, null, { params });
  }

  invoicePreview(processId: number): Observable<BillingProcessInvoicePreview> {
    return this.http.get<BillingProcessInvoicePreview>(
      `${this.baseUrl}/${processId}/invoice-preview`);
  }

  customerGroups(processId: number): Observable<BillingProcessCustomerGroup[]> {
    return this.http.get<BillingProcessCustomerGroup[]>(
      `${this.baseUrl}/${processId}/customer-groups`);
  }
}
